import AppBar from '@mui/material/AppBar';
import { visuallyHidden } from '@mui/utils';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {Link} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {useCallback, useEffect, useState} from 'react';
import gt_logo from '../../asset/images/gt-dark-logo.png';
import sz_logo from '../../asset/images/sz-dark-logo.png';
import nr_logo from '../../asset/images/nr-dark-logo.png';
import logo from '../../asset/images/neptune-dark-logo.png';
import {
  companyIdSelector,
  companyIsImpersonateSelector,
  isCompanySuspendedSelector,
  isGTSelector,
  isNRSelector,
  isSZSelector,
} from '../../store/Slices/companySlice';
import HeaderProfileButton from './HeaderProfileButton';
import { useStoreSelector } from '../../store/configstore';
import { ICompanyDetails } from '../../dto/Company.dto';
import { apiGet } from '../../axiosConfig';
import {
  isInternalSelector,
  logout,
  selectedUserCompany,
  userCompaniesSelector,
} from '../../store/Slices/userSlice';

import IconMenuButton from '../Button/IconMenuButton';

const AccountSuspendedText = () => {
  const isCompanySuspended = useSelector(isCompanySuspendedSelector);
  const isImpersonatedUser = useSelector(companyIsImpersonateSelector);

  return isCompanySuspended && isImpersonatedUser ? (
    <Typography
      variant='body1'
      alignContent={'center'}
      flexDirection={'column'}
      color={'danger'}
      className='mx-4'
      style={{ color: 'red', fontWeight: 'bold' }}>
      Account Suspended
    </Typography>
  ) : null;
};

const Header = () => {
  const companyIsImpersonate = useSelector(companyIsImpersonateSelector);
  const isInternal = useSelector(isInternalSelector);
  const isGT = useSelector(isGTSelector);
  const isNR = useSelector(isNRSelector);
  const isSz = useSelector(isSZSelector);
  const userImpersonateToken = sessionStorage.getItem('access_token');
  const userImpersonateUserId = sessionStorage.getItem('impersonated_user');

  const isImpersonatedUser = Boolean(
    companyIsImpersonate || (userImpersonateToken && userImpersonateUserId),
  );

  return (
    <AppBar
      elevation={0}
      position='fixed'
      className='blue-variant4 border-bottom-1'>
      <Grid container className='h-92'>
        <Grid
          className='flex leftbar border-right-1 w-full items-center pl-10'
          item>
          {isInternal && <IconMenuButton className='mr-8' />}
          <Typography component='h1' sx={visuallyHidden}>
            Voyager
          </Typography>
          <Link to='/dashboard' className='flex h-full items-center'>
            <img
              className='w-full navbar-brand'
              src={isGT ? gt_logo : isNR ? nr_logo : isSz ? sz_logo : logo}
              alt='logo'
            />
          </Link>
        </Grid>
        <Grid className='rightbar flex pr-10' item>
          <AccountSuspendedText />
          <Box sx={{ flexGrow: 1 }} className='flex items-center justify-center'>
            <UserCompanyDropDown isImpersonatedUser={isImpersonatedUser} />
            {isImpersonatedUser && (
              <Button variant='outlined' color='warning' onClick={window.close}>
                End Session
              </Button>
            )}
          </Box>
          <AccountSuspendedText />
          <HeaderProfileButton />
        </Grid>
      </Grid>
    </AppBar>
  );
};
export default Header;

const UserCompanyDropDown = ({
  isImpersonatedUser,
}: {
  isImpersonatedUser: boolean;
}) => {
  const companyId = useStoreSelector(companyIdSelector);
  const companies = useSelector(userCompaniesSelector);
  const isPartner = !useSelector(isInternalSelector);
  const [openDialog, setOpenDialog] = useState(false);
  const globalDispatch = useDispatch();
  const [selectedCompany, setselectedCompany] = useState<ICompanyDetails>(
    {} as ICompanyDetails,
  );

  const dispatch = useDispatch();

  const handleOnChange = useCallback(
    (value: ICompanyDetails | null) => {
      if (value) {
        apiGet(`/v1/user/change/company/${value.id}`).then(res => {
          dispatch(selectedUserCompany(res.data.data));
          window.location.reload();
        });
      }
    },
    [dispatch],
  );

  useEffect(() => {
    const noActiveCompanies =
      !companies ||
      companies.length === 0 ||
      !companies.some(c => c.pivot?.is_active && !c.suspended_at);

    setOpenDialog(isPartner && noActiveCompanies);

    if (noActiveCompanies) return;

    const activeCompany = companies.find(
      c => c.id === companyId && c.pivot?.is_active,
    );

    if (activeCompany) {
      setselectedCompany(activeCompany);
    }
  }, [companies, companyId, isPartner]);

  const handleCloseDialog = useCallback(() => {
    if (isImpersonatedUser) {
      window.close();
    } else {
      globalDispatch(logout());
    }
    setOpenDialog(false);
  }, [globalDispatch, isImpersonatedUser]);

  return (
    <>
      {isPartner &&
        companies &&
        companies.length > 1 &&
        companies?.some(c => !Boolean(c.suspended_at)) ? (
        <Autocomplete
          sx={{ width: '500px' }}
          id='tags-outlined'
          onChange={(_, value) => handleOnChange(value)}
          value={selectedCompany || null}
          options={companies ?? []}
          popupIcon={<ArrowDropDownIcon role='presentation' />}
          getOptionLabel={(option: ICompanyDetails) => {
            return option.name;
          }}
          getOptionDisabled={(option: ICompanyDetails) =>
            Boolean(option.suspended_at || !option.pivot?.is_active)
          }
          renderOption={(props, option: ICompanyDetails) => {
            return (
              <Typography {...props}>
                {option.name}
                {option.suspended_at ? <i className='ml-2'>- suspended</i> : ''}
              </Typography>
            );
          }}
          renderInput={params => <TextField {...params} label='Companies' />}
        />
      ) : null}
      {openDialog ? (
        <DialogDenied open={openDialog} closeDialog={handleCloseDialog} />
      ) : null}
    </>
  );
};

const DialogDenied = ({
  open,
  closeDialog,
}: {
  open: boolean;
  closeDialog: () => void;
}) => {
  return (
    <Dialog
      maxWidth='xs'
      fullWidth
      open={open}
      className='flex items-center justify-center'
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(10px) !important',
          backgroundColor: 'rgba(255, 255, 255, 0.2) !important', // light glass effect
        },
      }}>
      <DialogContent className='text-center mx-3'>
        <Typography className='font-bold text-24 text-center'>
          Account Disabled
        </Typography>
        <Typography className='text-18 mt-4 my-3'>
          Your account has been disabled. Please contact the support team at
          903-630-7487
        </Typography>
      </DialogContent>
      <DialogActions className='justify-center'>
        <Button variant='outlined' onClick={closeDialog}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};
